'use client';

import React, { useMemo, useState, useCallback, useRef } from 'react';
import { useParams } from 'next/navigation';
import {
  useGetCompanyUserQuery,
  useUpdateManagerReportsMutation,
  useGetCompanyUsersPaginatedInfiniteQuery,
} from 'src/api/redux';
import { Button, Checkbox, Search } from 'src/components';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useAuthorization } from 'src/hooks/authorization';
import { useDebounce } from 'use-debounce';

interface ReportsTabContentProps {
  selectedReports: Set<number>;
  setSelectedReports: React.Dispatch<React.SetStateAction<Set<number>>>;
  isDirty: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  managerReports: number[];
  resetState: () => void;
}

const ReportsTabContent = ({
  selectedReports,
  setSelectedReports,
  isDirty,
  setIsDirty,
  managerReports,
  resetState,
}: ReportsTabContentProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const log = useLogging();
  const { setNotification } = useNotification();
  const { userManagementCapabilities } = useAuthorization();
  // Assigning direct reports is a write on the company user (MKT-411).
  const { canWrite } = userManagementCapabilities;

  // Get IDs from route params
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();

  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const { refetch: mutateUser } = useGetCompanyUserQuery(
    { companyId, userId },
    { skip: !companyId || !userId }
  );

  const [updateManagerReports] = useUpdateManagerReportsMutation();

  // Infinite query for paginated users
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetCompanyUsersPaginatedInfiniteQuery(
    {
      companyId,
      q: debouncedSearch || undefined,
      sort: 'first_name',
      order: 'asc',
    },
    { skip: !companyId }
  );

  // Flatten all loaded pages into single array
  const users = useMemo(() => {
    if (!usersData?.pages) return [];
    return usersData.pages.flatMap((page) => page.results);
  }, [usersData?.pages]);

  // Total count from first page
  const totalCount = usersData?.pages?.[0]?.count ?? 0;

  // Clear all selected reports
  const handleOnClearReports = () => {
    setSelectedReports(new Set());
    setIsDirty(true);
  };

  // Reset form to initial state
  const handleCancel = () => {
    setSelectedReports(new Set(managerReports));
    setIsDirty(false);
  };

  // Submit form data
  const handleSubmit = async () => {
    if (!selectedReports || !companyId || !userId) return;
    setIsLoading(true);
    try {
      const data = { direct_reports: Array.from(selectedReports) };

      await updateManagerReports({
        companyId,
        managerId: userId,
        ...data,
      });

      log.event('Manager Reports Updated', {
        source: 'ManagerCard',
        companyId,
        data: data,
      });
      setNotification({
        title: 'Success',
        message: 'Manager reports updated',
        type: 'success',
      });
      // Reset dirty state after successful save
      setIsDirty(false);
    } catch (err) {
      log.exception('Error updating manager reports');
      setNotification({
        title: 'Error',
        message:
          (err as { error_message?: string })?.error_message ||
          'Error updating manager reports',
        type: 'error',
      });
    } finally {
      mutateUser();
      setIsLoading(false);
    }
  };

  // Handle individual checkbox change
  const handleChange = useCallback((userId: number, isChecked: boolean) => {
    setSelectedReports((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
    setIsDirty(true);
  }, []);

  // Handle select page (currently loaded users)
  const handleSelectPage = useCallback(() => {
    const visibleIds = users.map((u) => u.id);
    const allVisibleSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedReports.has(id));

    setSelectedReports((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // Deselect only visible items
        visibleIds.forEach((id) => next.delete(id));
      } else {
        // Select all visible items
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setIsDirty(true);
  }, [users, selectedReports]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when near bottom (50px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Check states for checkboxes
  const allVisibleSelected =
    users.length > 0 && users.every((u) => selectedReports.has(u.id));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center pt-4">
          <Search
            placeholder="Search reports"
            value={search}
            onSearch={(value) => setSearch(value)}
            className="w-full rounded-full max-w-md"
            data-testid="report-search"
            aria-label="Search for reports"
          />
          {canWrite && (
            <Button
              variant="plain"
              color="primary"
              onClick={handleOnClearReports}
              className="text-sm"
              aria-label="Clear all selected reports"
              data-testid="clear-reports-button"
            >
              clear all
            </Button>
          )}
        </div>

        {/* Selection controls */}
        <div className="flex items-center gap-4 px-3 py-2 bg-neutral-100 rounded-lg">
          <Checkbox
            size="sm"
            checked={allVisibleSelected}
            onChange={handleSelectPage}
            disabled={!canWrite}
            aria-label="Select all users on current page"
            data-testid="select-page-checkbox"
          />
          <span className="text-sm text-neutral-600">
            {hasNextPage
              ? `Select loaded (${users.length} of ${totalCount})`
              : `Select all (${totalCount})`}
          </span>

          {selectedReports.size > 0 && (
            <>
              <span className="text-neutral-300">|</span>
              <span
                className="text-sm text-neutral-600"
                data-testid="selected-count"
                aria-live="polite"
              >
                {selectedReports.size} selected
              </span>
            </>
          )}
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="bg-neutral-50 rounded-lg divide-y divide-neutral-200 h-48 overflow-hidden overflow-y-auto border border-neutral-200"
          aria-label="reports"
          role="list"
        >
          {isUsersLoading ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-sm text-neutral-500">Loading...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-sm text-neutral-500">No users found</span>
            </div>
          ) : (
            <>
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between px-3 py-2 first:rounded-t-lg last:rounded-b-lg hover:bg-neutral-100 transition-colors"
                  role="listitem"
                >
                  <span className="text-sm text-neutral-700">
                    {`${user.first_name} ${user.last_name}`}
                  </span>
                  <Checkbox
                    size="sm"
                    checked={selectedReports.has(user.id)}
                    onChange={(e) => handleChange(user.id, e.target.checked)}
                    disabled={!canWrite}
                    aria-label={`Select ${user.first_name} ${user.last_name}`}
                    data-testid={`user-checkbox-${user.id}`}
                  />
                </div>
              ))}
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-2">
                  <span className="text-sm text-neutral-500">
                    Loading more...
                  </span>
                </div>
              )}
              {!hasNextPage && users.length > 0 && (
                <div className="flex items-center justify-center py-2 text-xs text-neutral-400">
                  All {totalCount} users loaded
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {canWrite && (
        <div className="flex justify-between items-center border-t border-neutral-200 pt-4">
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleCancel}
            disabled={!isDirty}
            aria-label="Cancel changes"
          >
            Cancel
          </Button>
          <Button
            loading={isLoading}
            variant="solid"
            color="primary"
            onClick={handleSubmit}
            disabled={!isDirty}
            aria-label="Update manager reports"
          >
            Update
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReportsTabContent;
